import { GameLayout } from "./shared/GameLayout";
import { PrivateTrigger } from "./shared/PrivatePanel";
import {
  GameArt,
  PrivateInfo,
  TargetAction,
  TableProgress,
  FinishedRoles,
  BallotTally,
  PersonalOutcomeLine,
  RoleGrid,
  draftScope,
} from "./shared/PresenterParts";
import { personalOutcome } from "./outcome";
import { playerName, type PlayProps } from "./types";
export function Mafia(props: PlayProps) {
  const { state, send, isDisplay } = props,
    g = state.game,
    p = g.public,
    s = g.private,
    players = p.players ?? [],
    alive = players.filter((v: any) => v.alive !== false),
    n = (id: string) => playerName(players, id),
    night = g.phase === "night",
    can = (a: string) =>
      !isDisplay && !state.isSpectator && g.legalActions.includes(a),
    winner = g.winner as { reason?: string; team?: string } | undefined;
  const title = g.finished
    ? winner?.team === "mafia"
      ? "The Mafia wins."
      : "The town wins."
    : night
      ? "The town is asleep."
      : g.phase === "dawn"
        ? "Morning has arrived."
        : g.phase === "vote"
          ? "Who do you suspect?"
          : "The verdict is in.";
  const nightLabel =
    s.role === "doctor"
      ? "Protect player"
      : s.role === "detective"
        ? "Investigate player"
        : "Confirm night choice";
  const last = g.phase === "verdict" ? p.lastVote : p.lastNight;
  const outcome = last
    ? last.eliminatedId
      ? `${n(last.eliminatedId)} ${g.phase === "verdict" ? "was eliminated." : "did not survive the night."}`
      : g.phase === "verdict"
        ? "No elimination. The vote did not reach a majority."
        : "Everyone survived the night."
    : null;
  const needed = Math.floor(alive.length / 2) + 1;
  const publicAudience = isDisplay || state.isSpectator;
  const scope = draftScope(state);
  const viewerId = publicAudience ? null : state.playerId;
  return (
    <GameLayout
      {...props}
      composition={night ? "stacked" : "split"}
      contextArt={night ? <GameArt kind="mafia-night" /> : undefined}
      phaseLabel={`${night ? "Night" : "Day"} ${g.round}`}
      phaseNote={
        g.phase === "vote"
          ? `${needed} votes needed`
          : `${alive.length} of ${players.length} still in`
      }
      title={title}
      publicTitle={title}
      progressLabel="voted"
      description={
        g.finished
          ? winner?.reason
          : night
            ? "Eyes on your own card until morning."
            : null
      }
      publicDescription={g.finished ? winner?.reason : null}
      board={
        <>
          {!night ? (
            <GameArt
              kind="mafia-day"
              variant={can("vote") || g.finished ? "stamp" : "band"}
            />
          ) : null}
          {g.finished ? (
            <PersonalOutcomeLine
              outcome={personalOutcome(g, state.playerId, publicAudience)}
            />
          ) : null}
          {!night && (g.finished || outcome) ? (
            <div
              className={`play-announcement${last?.eliminatedId || g.finished ? " is-loss" : " is-safe"}`}
              data-tour="announcement"
            >
              {g.finished ? (
                <>
                  {p.lastNight ? (
                    <p>
                      <strong>Last night: </strong>
                      {p.lastNight.eliminatedId
                        ? `${n(p.lastNight.eliminatedId)} did not survive.`
                        : "Everyone survived."}
                    </p>
                  ) : null}
                  {p.lastVote ? (
                    <p>
                      <strong>Last vote: </strong>
                      {p.lastVote.eliminatedId
                        ? `${n(p.lastVote.eliminatedId)} was eliminated.`
                        : "No elimination."}
                    </p>
                  ) : null}
                </>
              ) : (
                <p className="announce-lead">{outcome}</p>
              )}
            </div>
          ) : null}
          {g.phase === "verdict" && p.lastVote?.ballots?.length ? (
            <BallotTally
              players={players}
              ballots={p.lastVote.ballots}
              viewerId={viewerId}
            />
          ) : null}
          {g.finished ? (
            <RoleGrid state={state} viewerId={viewerId} />
          ) : g.phase === "verdict" && p.lastVote?.ballots?.length ? null : night ||
            publicAudience ||
            !can("vote") ? (
            <TableProgress state={state} />
          ) : null}
          <FinishedRoles state={state} />
        </>
      }
      action={
        night && !isDisplay ? (
          <PrivateTrigger>Open your card</PrivateTrigger>
        ) : can("vote") ? (
          <TargetAction
            players={players}
            ids={
              s.eligibleTargetIds ??
              alive
                .filter((v: any) => v.id !== state.playerId)
                .map((v: any) => v.id)
            }
            type="vote"
            label="Submit secret vote"
            emptyLabel="Pick someone to vote out"
            send={send}
            scope={scope}
            allowAbstain
          />
        ) : null
      }
      privateContent={<PrivateInfo state={state} />}
      privateAction={
        can("night") ? (
          <>
            <p className="private-instruction">
              {s.role === "doctor"
                ? "Choose someone to protect tonight."
                : s.role === "detective"
                  ? "Choose whose allegiance to investigate."
                  : "Choose a target. Your team needs a strict majority."}
            </p>
            <TargetAction
              players={players}
              ids={s.eligibleTargetIds ?? []}
              type="night"
              label={nightLabel}
              emptyLabel={
                s.role === "doctor"
                  ? "Pick someone to protect"
                  : s.role === "detective"
                    ? "Pick someone to investigate"
                    : "Pick a target"
              }
              send={send}
              scope={scope}
              allowAbstain={s.canAbstain}
            />
          </>
        ) : undefined
      }
    />
  );
}
